import { useState } from 'react'

import {
  Box,
  Card,
  Circle,
  Icon,
  Text,
  colors as gammaColors,
} from '@factorialco/deprecated-design-system'
import { ResponsiveFunnel } from '@nivo/funnel'
import { AtsHiringStagesInsightsFunnelChartFragment } from 'generated/resources/graphql'
import { sumBy } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import { UNASSIGNED_STAGE, getProperties } from 'modules/ats/lib/utils/hiringStages'

import { HelpSidepanel } from './HelpSidepanel'

interface Props {
  stagesData: AtsHiringStagesInsightsFunnelChartFragment[]
}

export const FunnelChart = ({ stagesData }: Props) => {
  const [openSidepanel, setOpenSidepanel] = useState<boolean>(false)
  const totalCandidates = sumBy(stagesData, 'candidatesAmount')
  const data = stagesData.flatMap(({ hiringStage, candidatesAmount }) =>
    !hiringStage
      ? []
      : [
          {
            id: hiringStage.id,
            color: gammaColors[getProperties(hiringStage.name).color],
            label: hiringStage.label,
            value: candidatesAmount,
            percentage: Math.round((candidatesAmount / totalCandidates) * 100),
          },
        ]
  )

  const chartColors = data.map((item) => item.color)
  const hasData = data.length > 0

  return (
    <>
      <Card>
        <Card.Title tooltip={i18n.t('ats.insights_page.hiring_stages_funnel_chart.titleTooltip')}>
          {i18n.t('ats.insights_page.hiring_stages_funnel_chart.title')}
        </Card.Title>

        <Card.Content>
          {hasData ? (
            <Box paddingY="s24" width="full">
              <Box flexDirection="row" width="full" justifyContent="spaceBetween">
                {data.map((item) => (
                  <Box
                    key={`funnel-chart-${item.id}`}
                    textAlign="center"
                    paddingX="s16"
                    paddingBottom="s16"
                    gap="s4"
                    style={{ flex: '1' }}
                  >
                    <Text.Normal size="200" color="grey800" weight="medium">
                      {item.label}
                    </Text.Normal>
                    <Text.Normal size="200" color="grey1000" weight="semibold">
                      {`${item.value} (${item.percentage}%)`}
                    </Text.Normal>
                  </Box>
                ))}
              </Box>
              <Box height="s360" width="full">
                <ResponsiveFunnel
                  data={data}
                  direction="horizontal"
                  interpolation="linear"
                  shapeBlending={0}
                  borderWidth={0}
                  enableLabel={false}
                  beforeSeparatorLength={60}
                  afterSeparatorLength={60}
                  currentBorderWidth={10}
                  colors={chartColors}
                  theme={{
                    grid: {
                      line: {
                        stroke: gammaColors['grey400'],
                        strokeWidth: 1,
                      },
                    },
                  }}
                  tooltip={(tooltipData) => (
                    <Box
                      background="grey800"
                      padding="s12"
                      borderRadius={{ all: 'abs008' }}
                      gap="s8"
                      alignItems="center"
                    >
                      {tooltipData.part.data?.label === UNASSIGNED_STAGE ? (
                        <Box textAlign="center" maxWidth="s240">
                          <Text.Normal color="grey000White" size="200" weight="medium">
                            {i18n.t(
                              'ats.insights_page.hiring_stages_funnel_chart.unassigned_tooltip'
                            )}
                          </Text.Normal>
                        </Box>
                      ) : (
                        <Box flexDirection="row" alignItems="center" gap="s8">
                          <div>
                            <Circle color={tooltipData.part.data?.color} size={16} />
                          </div>
                          <Text.Normal color="grey000White" size="200" weight="medium">
                            {i18n.t(
                              'ats.insights_page.hiring_stages_funnel_chart.candidates_percentage',
                              {
                                label: tooltipData.part.data?.label,
                                value: tooltipData.part.data?.value,
                                percentage: tooltipData.part.data?.percentage,
                              }
                            )}
                          </Text.Normal>
                        </Box>
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Box>
          ) : (
            <Box paddingY="s24" width="full">
              <Box
                maxWidth="s600"
                gap="s8"
                textAlign="center"
                alignItems="center"
                alignSelf="center"
              >
                <Icon.Large icon="document-folder-drawer-2" color="grey600" />
                <Text.Header2 color="grey1000">
                  {i18n.t('ats.insights_page.hiring_stages_funnel_chart.no_data.title')}
                </Text.Header2>
                <Text.Normal size="300" color="grey800">
                  {i18n.t('ats.insights_page.hiring_stages_funnel_chart.no_data.description')}
                </Text.Normal>
              </Box>
            </Box>
          )}
        </Card.Content>
        {hasData && (
          <Card.Link
            to="#"
            onClick={() => {
              setOpenSidepanel(true)
            }}
          >
            {i18n.t('ats.insights_page.hiring_stages_funnel_chart.linkLabel')}
          </Card.Link>
        )}
      </Card>
      {hasData && <HelpSidepanel setOpen={setOpenSidepanel} open={openSidepanel} />}
    </>
  )
}
